import { PageHeader } from "@finitless/shared/components";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTimezoneFromLocation } from "@finitless/shared/lib";

import {
  useGoogleMaps,
  useBusinessUpdate,
  useBusinessPaymentMethods,
  useBusinessZones,
} from "../../../../hooks";
import {
  Button,
  Icon,
  Input,
  CustomGoogleAutocompleteInput,
} from "../../../ui";
import Breadcrumb from "../../../ui/Navigation/Breadcrumb";
import SimpleMap from "../../../ui/UIElements/SimpleMap";

import {
  PaymentMethodsStep,
  PhotosStep,
  ScheduleStep,
  TaxesStep,
  BusinessZonesStep,
} from "../../../ui/Business";
import "../../../catalog-ordering.css";

// Memoized map component to prevent unnecessary re-renders
const MemoizedSimpleMap = React.memo(SimpleMap);

const BusinessDetailPage = () => {
  const { t } = useTranslation("stores");
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    businessData,
    isLoadingBusiness,
    businessError,
    updateBusiness,
    isUpdating,
  } = useBusinessUpdate(id);
  const {
    paymentMethods,
    addPaymentMethod,
    isAddingPaymentMethod,
    isLoadingPaymentMethods,
    togglePaymentMethod,
  } = useBusinessPaymentMethods(id);
  const {
    toggleZone,
    createZone,
    updateZoneDetails,
    deleteZone,
    updateZoneBusinesses,
  } = useBusinessZones(id);

  const [currentStep, setCurrentStep] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [isLoadingTimezone, setIsLoadingTimezone] = useState(false);
  const [hydrationReady, setHydrationReady] = useState(false);

  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Check if Google Maps is loaded
  useGoogleMaps();

  // Initialize form data when business data loads
  useEffect(() => {
    if (businessData) {
      setFormData({
        name: businessData.name || "",
        slug: businessData.slug || "",
        address: businessData.address || "",
        cellphone: String(businessData.cellphone || "").replace(/\D/g, ""),
        timezone: businessData.timezone || "",
        price_level: businessData.price_level || "1",
        location: businessData.location || { lat: 0, lng: 0, zipcode: -1 },
      });

      // Set logo file if available
      if (businessData.logo) {
        setLogoFile({ url: businessData.logo });
      }

      // Set schedule if available - parse if it's a JSON string
      if (businessData.schedule) {
        try {
          const parsedSchedule =
            typeof businessData.schedule === "string"
              ? JSON.parse(businessData.schedule)
              : businessData.schedule;
          setSchedule(parsedSchedule);
        } catch (error) {
          console.error("Error parsing schedule:", error);
          setSchedule([]);
        }
      }

      setHydrationReady(true);
    }
  }, [businessData]);

  // Handle Google Address Selection
  const handleGoogleAddressSelection = async (addressData) => {
    if (addressData?.map_data?.place_id) {
      // Update address
      setFormData((prev) => ({
        ...prev,
        address: addressData.address,
      }));

      // Update location coordinates
      if (addressData.location) {
        setFormData((prev) => ({
          ...prev,
          location: {
            lat: addressData.location.lat,
            lng: addressData.location.lng,
            zipcode: addressData.zipcode || -1,
          },
        }));

        // Auto-populate timezone from coordinates using Google Maps Time Zone API
        setIsLoadingTimezone(true);
        try {
          if (GOOGLE_MAPS_API_KEY) {
            const timezone = await getTimezoneFromLocation(
              addressData.location.lat,
              addressData.location.lng,
              GOOGLE_MAPS_API_KEY,
            );

            if (timezone) {
              setFormData((prev) => ({
                ...prev,
                timezone: timezone,
              }));
              // Clear timezone error if it was set
              if (formErrors.timezone) {
                setFormErrors((prev) => ({
                  ...prev,
                  timezone: "",
                }));
              }
            } else {
              console.warn("No timezone found for location");
            }
          } else {
            console.warn(
              "Google Maps API key not available for timezone detection",
            );
          }
        } catch (error) {
          console.error("Error fetching timezone:", error);
        } finally {
          setIsLoadingTimezone(false);
        }
      }

      // Update timezone if available from address data (fallback)
      if (addressData.timezone && !formData.timezone) {
        setFormData((prev) => ({
          ...prev,
          timezone: addressData.timezone,
        }));
      }

      // Clear any location errors
      if (formErrors.location) {
        setFormErrors((prev) => ({
          ...prev,
          location: "",
        }));
      }
    }
  };

  const steps = [
    {
      id: 1,
      title: t("detailForm.steps.basicInfo.title"),
      description: t("detailForm.steps.basicInfo.description"),
    },
    {
      id: 2,
      title: t("detailForm.steps.location.title"),
      description: t("detailForm.steps.location.description"),
    },
    {
      id: 3,
      title: t("detailForm.steps.paymentMethods.title"),
      description: t("detailForm.steps.paymentMethods.description"),
    },
    {
      id: 4,
      title: t("detailForm.steps.taxes.title"),
      description: t("detailForm.steps.taxes.description"),
    },
    {
      id: 7,
      title: t("detailForm.steps.businessZones.title"),
      description: t("detailForm.steps.businessZones.description"),
    },
    {
      id: 5,
      title: t("detailForm.steps.businessHours.title"),
      description: t("detailForm.steps.businessHours.description"),
    },
    {
      id: 6,
      title: t("detailForm.steps.logo.title"),
      description: t("detailForm.steps.logo.description"),
    },
  ];

  const stepIcons = {
    1: "Info",
    2: "MapPin",
    3: "CreditCard",
    4: "Percent",
    5: "Clock",
    6: "Image",
    7: "Map",
  };

  // Detect if there are changes to enable/disable Update button
  const hasChanges = useMemo(() => {
    if (!businessData || !hydrationReady) return false;

    // Basic fields
    if (formData.name !== businessData?.name) return true;
    if (formData.slug !== businessData?.slug) return true;

    const normalizedFormPhone = String(formData.cellphone ?? "").replace(
      /\D/g,
      "",
    );
    const normalizedExistingPhone = String(
      businessData?.cellphone ?? "",
    ).replace(/\D/g, "");
    if (normalizedFormPhone !== normalizedExistingPhone) return true;

    // Location-related
    if (formData.address !== businessData?.address) return true;
    if (
      JSON.stringify(formData.location) !==
      JSON.stringify(businessData?.location)
    )
      return true;
    if (formData.timezone !== businessData?.timezone) return true;

    // Price level (normalize to string for comparison)
    if (
      String(formData.price_level ?? "") !==
      String(businessData?.price_level ?? "")
    )
      return true;

    // Logo changes
    if (logoFile?.base64) return true;
    if (logoFile?.url === null && businessData?.logo) return true;

    // Schedule changes (compare normalized arrays)
    let existingSchedule = [];
    if (businessData?.schedule) {
      try {
        existingSchedule =
          typeof businessData.schedule === "string"
            ? JSON.parse(businessData.schedule)
            : businessData.schedule || [];
      } catch {
        existingSchedule = [];
      }
    }

    const currentScheduleStr = JSON.stringify(schedule || []);
    const existingScheduleStr = JSON.stringify(existingSchedule || []);
    if (currentScheduleStr !== existingScheduleStr) return true;

    return false;
  }, [businessData, formData, logoFile, schedule, hydrationReady]);

  const breadcrumbItems = [
    { label: t("detailForm.breadcrumb.menu"), path: "/menu", icon: "Menu" },
    { label: `${businessData?.name}`, active: true },
  ];

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle phone number input - only allow numbers and max 15 digits
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;

    // Only allow numbers
    const numericValue = value.replace(/\D/g, "");

    // Limit to 15 digits
    if (numericValue.length <= 15) {
      handleFieldChange("cellphone", numericValue);
    }
  };

  const handleLogoUpload = (fileData) => {
    setLogoFile(fileData);
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
  };

  const handleSubmit = async () => {
    try {
      // Prepare update payload - only include changed fields
      const payload = {};

      // Check if basic fields have changed
      if (formData.name !== businessData?.name) {
        payload.name = formData.name;
      }
      if (formData.slug !== businessData?.slug) {
        payload.slug = formData.slug;
      }
      if (formData.cellphone) {
        const cellphoneValue = parseInt(formData.cellphone, 10);
        if (cellphoneValue !== businessData?.cellphone) {
          payload.cellphone = cellphoneValue;
        }
      }

      // Check if location fields have changed
      if (formData.address !== businessData?.address) {
        payload.address = formData.address;
      }
      if (
        JSON.stringify(formData.location) !==
        JSON.stringify(businessData?.location)
      ) {
        payload.location = JSON.stringify(formData.location);
      }
      if (formData.timezone !== businessData?.timezone) {
        payload.timezone = formData.timezone;
      }

      // Check if price level has changed
      if (formData.price_level !== businessData?.price_level) {
        payload.price_level = formData.price_level;
      }

      // Check if logo has changed
      // Only include logo if it's a new upload (base64) or if it was removed
      if (logoFile?.base64) {
        payload.logo = logoFile.base64;
      } else if (logoFile?.url === null && businessData?.logo) {
        // Logo was removed
        payload.logo = "";
      }

      // Include schedule ONLY if it changed
      let existingSchedule = [];
      if (businessData?.schedule) {
        try {
          existingSchedule =
            typeof businessData.schedule === "string"
              ? JSON.parse(businessData.schedule)
              : businessData.schedule || [];
        } catch {
          existingSchedule = [];
        }
      }
      const currentScheduleStr = JSON.stringify(schedule || []);
      const existingScheduleStr = JSON.stringify(existingSchedule || []);
      if (currentScheduleStr !== existingScheduleStr) {
        payload.schedule = currentScheduleStr;
      }

      // If no changes, don't make the request
      if (Object.keys(payload).length === 0) {
        navigate("/menu");
        return;
      }

      const result = await updateBusiness(payload);
      if (result) {
        navigate("/menu");
      }
    } catch (error) {
      console.error("Error updating business:", error);
    }
  };

  const renderStepContent = (stepId) => {
    switch (stepId) {
      case 1:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("detailForm.fields.businessName.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Input
                type="text"
                value={formData.name || ""}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                placeholder={t("detailForm.fields.businessName.placeholder")}
                className="h-11 w-full"
                error={formErrors.name}
              />
            </div>

            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("detailForm.fields.slug.label")}
              </label>
              <Input
                type="text"
                value={formData.slug || ""}
                onChange={() => {}} // No-op since it's disabled
                placeholder={t("detailForm.fields.slug.placeholder")}
                className="h-11 w-full"
                disabled={true}
              />
            </div>

            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("detailForm.fields.phone.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Input
                type="text"
                value={formData.cellphone || ""}
                onChange={handlePhoneNumberChange}
                placeholder={t("detailForm.fields.phone.placeholder")}
                className="h-11 w-full"
                error={formErrors.cellphone}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t("detailForm.fields.phone.helper")}
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("detailForm.fields.address.label")} *
              </label>
              <div className="relative">
                <CustomGoogleAutocompleteInput
                  apiKey={GOOGLE_MAPS_API_KEY}
                  value={formData.address || ""}
                  onChangeAddress={handleGoogleAddressSelection}
                  placeholder={t("detailForm.fields.address.placeholder")}
                  types={["address"]}
                  fields={[
                    "place_id",
                    "formatted_address",
                    "geometry",
                    "address_components",
                  ]}
                  countryCode="*"
                  className={`bg-secondary focus:ring-ring h-11 w-full rounded-lg border px-3 py-2 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none ${
                    formErrors.address ? "border-destructive" : "border-border"
                  }`}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <Icon
                    name="MapPin"
                    className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                  />
                </div>
              </div>
              <p className="text-muted-foreground mt-2 text-xs sm:text-sm">
                {t("detailForm.fields.address.helper")}
              </p>
              {formErrors.address && (
                <p className="text-destructive mt-2 text-xs sm:text-sm">
                  {formErrors.address}
                </p>
              )}
            </div>

            {/* Map Display - stays mounted to preserve state when accordion closes */}
            {formData.location?.lat && formData.location?.lng && (
              <div>
                <label className="text-foreground mb-2 block text-sm font-medium">
                  {t("detailForm.fields.locationPreview.label")}
                </label>
                <div className="border-border bg-secondary relative h-48 w-full overflow-hidden rounded-lg border sm:h-64">
                  <MemoizedSimpleMap
                    lat={formData.location.lat}
                    lng={formData.location.lng}
                    zoom={15}
                  />
                  <div className="bg-opacity-50 absolute top-2 right-2 z-10 rounded bg-black px-2 py-1 text-xs text-white">
                    {formData.location.lat.toFixed(6)},{" "}
                    {formData.location.lng.toFixed(6)}
                  </div>
                </div>
                <p className="text-muted-foreground mt-2 text-xs sm:text-sm">
                  {t("detailForm.fields.locationPreview.helper")}
                </p>
              </div>
            )}

            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("detailForm.fields.timezone.label")}
              </label>
              <Input
                type="text"
                value={formData.timezone || ""}
                placeholder={t("detailForm.fields.timezone.placeholder")}
                className="h-11 w-full"
                disabled={true}
              />
              {isLoadingTimezone && (
                <p className="text-muted-foreground mt-1 text-xs">
                  {t("detailForm.fields.timezone.detecting")}
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <PaymentMethodsStep
            isEditing
            selectedPaymentMethods={paymentMethods}
            onPaymentMethodToggle={togglePaymentMethod}
            priceLevel={formData.price_level}
            onPriceLevelChange={(value) =>
              handleFieldChange("price_level", value)
            }
            addPaymentMethod={addPaymentMethod}
            isAddingPaymentMethod={isAddingPaymentMethod}
            errors={formErrors}
          />
        );

      case 4:
        return (
          <TaxesStep
            businessId={id}
            selectedTaxId={
              formData?.tax_id ?? businessData?.tax_id ?? businessData?.tax?.id
            }
            onAssigned={(tax) =>
              setFormData((prev) => ({ ...prev, tax_id: tax ? tax.id : null }))
            }
          />
        );

      case 5:
        return (
          <ScheduleStep schedule={schedule} onScheduleChange={setSchedule} />
        );

      case 6:
        return (
          <PhotosStep
            logoFile={logoFile}
            onLogoUpload={handleLogoUpload}
            onLogoRemove={handleLogoRemove}
          />
        );

      case 7:
        return (
          <BusinessZonesStep
            businessId={parseInt(id, 10)}
            zones={businessData?.zones || []}
            deliveryTime={businessData?.delivery_time}
            pickupTime={businessData?.pickup_time}
            cancelOrderAfterMinutes={businessData?.cancel_order_after_minutes}
            orderDefaultPriority={businessData?.order_default_priority}
            minimum={businessData?.minimum}
            businessAddress={businessData?.address}
            businessLocation={businessData?.location}
            onUpdate={(payload) => updateBusiness(payload)}
            onToggleZone={(zoneId, enabled) => toggleZone(zoneId, enabled)}
            onCreateZone={(payload) => createZone(payload)}
            onUpdateZone={(zoneId, payload) =>
              updateZoneDetails({ zoneId, payload })
            }
            onDeleteZone={(zoneId) => deleteZone(zoneId)}
            onUpdateZoneBusinesses={(zoneId, businessIds) =>
              updateZoneBusinesses({ zoneId, businessIds })
            }
            isSaving={isUpdating}
          />
        );

      default:
        return null;
    }
  };

  if (isLoadingBusiness) {
    return (
      <div
        className="ordering-catalog-surface flex min-h-screen items-center justify-center"
        data-ordering-expression="product"
      >
        <div className="text-center">
          <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2"></div>
          <p className="text-muted-foreground">
            {t("detailForm.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (businessError) {
    return (
      <div
        className="ordering-catalog-surface flex min-h-screen items-center justify-center"
        data-ordering-expression="product"
      >
        <div className="text-center">
          <Icon
            name="AlertCircle"
            className="text-destructive mx-auto mb-4 h-12 w-12"
          />
          <h3 className="mb-2 text-lg font-semibold text-white">
            {t("detailForm.states.errorTitle")}
          </h3>
          <p className="text-muted-foreground mb-4">
            {businessError.message || t("detailForm.states.errorDescription")}
          </p>
          <Button onClick={() => navigate("/menu")} variant="default">
            {t("detailForm.actions.backToMenu")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="ordering-catalog-surface space-y-4 sm:space-y-6"
      data-ordering-expression="product"
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Main Content */}
      <div className="border-border bg-background rounded-lg border">
        {/* Header */}
        <div className="border-border border-b p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <PageHeader
              title={t("detailForm.header.title")}
              subtitle={t("detailForm.header.subtitle")}
            />
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => navigate("/menu")}
                variant="outline"
                size="sm"
              >
                {t("common:actions.cancel")}
              </Button>
            </div>
          </div>
        </div>

        {/* Accordion Content */}
        <div className="space-y-3 p-4 sm:space-y-4 sm:p-6">
          {steps.map((step) => (
            <div
              key={step.id}
              className="border-border bg-background overflow-hidden rounded-lg border"
            >
              <Button
                variant="ghost"
                type="button"
                className={`hover:bg-card flex w-full items-center justify-between p-4 text-left transition-colors sm:p-5 ${currentStep === step.id ? "bg-card" : ""}`}
                onClick={() => {
                  setFormErrors({});
                  setCurrentStep((prev) => (prev === step.id ? null : step.id));
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-md ${currentStep === step.id ? "bg-primary" : "bg-secondary"}`}
                  >
                    <Icon
                      name={stepIcons[step.id]}
                      className="h-4 w-4 text-white"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-white sm:text-base">
                        {step.title}
                      </h3>
                      {step.id === 3 && isLoadingPaymentMethods && (
                        <span className="text-muted-foreground text-xs">
                          {t("common:states.loading")}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-0.5 text-xs sm:text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>
                <Icon
                  name={currentStep === step.id ? "ChevronUp" : "ChevronDown"}
                  className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                />
              </Button>
              {/* Special handling for Location step (id=2): use CSS to hide instead of unmounting */}
              {/* This preserves the Google Map state when accordion closes/opens */}
              {step.id === 2 ? (
                // Location step: keep mounted but hide with CSS when not active
                <div
                  className={`border-border border-t p-4 sm:p-6 ${currentStep !== step.id ? "hidden" : ""}`}
                >
                  {renderStepContent(step.id)}
                </div>
              ) : (
                // Other steps: use conditional rendering (unmount when closed)
                currentStep === step.id && (
                  <div className="border-border border-t p-4 sm:p-6">
                    {renderStepContent(step.id)}
                  </div>
                )
              )}
            </div>
          ))}
        </div>

        {/* Persistent Footer Actions */}
        <div className="ordering-catalog-actions border-border bg-background border-t p-4 pb-24 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <Button
              onClick={() => navigate("/menu")}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isUpdating || !hasChanges}
              className="w-full sm:w-auto"
            >
              {isUpdating
                ? t("detailForm.actions.updating")
                : t("detailForm.actions.updateBusiness")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailPage;
